import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { AddressInput } from '@/components/shared/AddressInput';
import { useCreateLocation, useLocation, useUpdateLocation } from '@/hooks/useLocations';
import { useManagers } from '@/hooks/useManagers';
import type { Location } from '@jobos/shared/types/location';
import { AddressPhotoField } from '@/components/shared/AddressPhotoField';

interface LocationFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: (location: Location) => void;
  clientId: string;
  defaultManagerId?: string;
  location?: Location | null;
  defaultValues?: {
    managerId?: string;
    name?: string;
    addressLine1?: string;
    addressLine2?: string;
    city?: string;
    state?: string;
    zip?: string;
  } | null;
}

export function LocationFormDialog({
  open,
  onOpenChange,
  onSuccess,
  clientId,
  defaultManagerId,
  location,
  defaultValues,
}: LocationFormDialogProps) {
  const [formData, setFormData] = useState({
    managerId: '',
    name: '',
    addressLine1: '',
    addressLine2: '',
    city: '',
    state: '',
    zip: '',
  });

  const { mutateAsync: createLocation, isPending: isCreating } = useCreateLocation();
  const { mutateAsync: updateLocation, isPending: isUpdating } = useUpdateLocation(location?.id || '');
  const { data: managers, isLoading: managersLoading } = useManagers(clientId);

  const isPending = isCreating || isUpdating;
  const isEditMode = !!location;

  // The list rows this dialog opens from carry no photo URL — only the detail
  // read presigns one — so fetch it here, and only while editing.
  const { data: locationDetail } = useLocation(isEditMode && open ? location!.id : undefined);

  // Reset form when location changes or dialog opens/closes
  useEffect(() => {
    console.log('LocationFormDialog useEffect triggered with location:', location);
    if (location) {
      // Edit mode - populate with existing data
      setFormData({
        managerId: location.managerId || '__none__',
        name: location.name || '',
        addressLine1: location.addressLine1 || '',
        addressLine2: location.addressLine2 || '',
        city: location.city,
        state: location.state || '',
        zip: location.zip || '',
      });
    } else if (open) {
      // Create mode - use defaults if provided, otherwise empty
      setFormData({
        managerId: defaultValues?.managerId || defaultManagerId || '',
        name: defaultValues?.name || '',
        addressLine1: defaultValues?.addressLine1 || '',
        addressLine2: defaultValues?.addressLine2 || '',
        city: defaultValues?.city || '',
        state: defaultValues?.state || '',
        zip: defaultValues?.zip || '',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultManagerId, location, open]);

  const handleSave = async () => {
    if (!formData.city.trim() || !formData.addressLine1.trim()) return;

    try {
      let savedLocation: Location | undefined;

      console.log('Saving location with data:', formData, 'isEditMode:', isEditMode);

      const managerId = formData.managerId === '__none__' ? null : formData.managerId || null;

      if (isEditMode) {
        // Update existing location - use null for empty fields to ensure they're cleared
        savedLocation = await updateLocation({
          managerId,
          name: formData.name.trim() || null,
          addressLine1: formData.addressLine1.trim() || null,
          addressLine2: formData.addressLine2.trim() || null,
          city: formData.city.trim(),
          state: formData.state.trim() || null,
          zip: formData.zip.trim() || null,
        });
      } else {
        // Create new location
        savedLocation = await createLocation({
          clientId,
          managerId: managerId || undefined,
          name: formData.name.trim() || undefined,
          addressLine1: formData.addressLine1.trim() || undefined,
          addressLine2: formData.addressLine2.trim() || undefined,
          city: formData.city.trim(),
          state: formData.state.trim() || undefined,
          zip: formData.zip.trim() || undefined,
        });
      }

      console.log('Location saved successfully:', savedLocation);

      onSuccess(savedLocation);
      onOpenChange(false);
    } catch (error) {
      console.error('Failed to save location:', error);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {isEditMode ? 'Edit Service Address' : 'Add New Service Address'}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Manager
            </label>
            <Select
              value={formData.managerId}
              onValueChange={(value: string) =>
                setFormData((prev) => ({ ...prev, managerId: value }))
              }
              disabled={managersLoading}
            >
              <SelectTrigger>
                <SelectValue
                  placeholder={
                    managersLoading
                      ? 'Loading managers...'
                      : 'Select a manager'
                  }
                />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">None (client acts as manager)</SelectItem>
                {managers?.map((manager) => (
                  <SelectItem key={manager.id} value={manager.id}>
                    {manager.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Nickname
            </label>
            <Input
              value={formData.name}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, name: e.target.value }))
              }
              placeholder="Plaza name or suite number (optional)"
            />
          </div>

          <AddressInput
            value={{
              addressLine1: formData.addressLine1,
              addressLine2: formData.addressLine2,
              city: formData.city,
              state: formData.state,
              zip: formData.zip,
            }}
            onChange={(address) =>
              setFormData((prev) => ({
                ...prev,
                addressLine1: address.addressLine1 || '',
                addressLine2: address.addressLine2 || '',
                city: address.city || '',
                state: address.state || '',
                zip: address.zip || '',
              }))
            }
            required={true}
            layout="compact"
          />
        </div>

        {/* Photo needs a record to attach to, so it appears once the address
            exists — on create, it's the first thing waiting on the detail page. */}
        {isEditMode && location && (
          <div className="pt-4 border-t">
            <label className="block text-sm font-medium text-gray-700 mb-1">Address Photo</label>
            <p className="text-xs text-gray-500 mb-2">
              Used as the cover photo on new inspection reports for this address.
            </p>
            <AddressPhotoField
              locationId={location.id}
              photoUrl={locationDetail?.photoUrl}
              compact
            />
          </div>
        )}

        <div className="flex justify-end gap-3 pt-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isPending}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleSave}
            disabled={isPending || !formData.city.trim() || !formData.addressLine1.trim()}
          >
            {isPending ? 'Saving...' : isEditMode ? 'Update Service Address' : 'Save Service Address'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
